import { useMemo, useState } from 'react'
import { MapPin, ChevronDown, Check } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { MOCK_LOCATIONS } from '@/shared/mock/data'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { useAllPlaces } from '@/features/lugares/use-places'


// Location-first control for the discovery screens — the feed is always centred
// on a city. Tapping opens a picker sheet.
//
// THE CITIES COME FROM THE REAL DIRECTORY since 2026-08-10. They were a
// hardcoded list of five (`MOCK_LOCATIONS`), and the seeded directory covers
// TWENTY-SEVEN: a player in Motril — 6 clubs, more than Granada — could not
// select their own city on either discovery screen, while the list offered
// Madrid and Maracena, where there are none. 48 venues were seeded across the
// province and three cities were reachable.
//
// Ordered by club count, because that is the order that helps: the city with
// the most padel is the one a player is most likely to want. Alphabetical would
// bury Motril under Albolote.
export function LocationBar({
  location,
  onLocationChange,
}: {
  location: string
  onLocationChange: (location: string) => void
}) {
  const [open, setOpen] = useState(false)
  // The bypass keeps the certified fixture list so design walks are stable.
  const directory = useAllPlaces()
  const cities = useMemo(() => {
    if (AUTH_BYPASS) return [...MOCK_LOCATIONS] as string[]
    const byCount = new Map<string, number>()
    for (const p of directory.data ?? []) {
      if (p.city) byCount.set(p.city, (byCount.get(p.city) ?? 0) + 1)
    }
    // The player's CURRENT city always appears, even with zero clubs — it is
    // where they are, and dropping it would leave the sheet showing no
    // selection at all.
    if (location && !byCount.has(location)) byCount.set(location, 0)
    return [...byCount.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([c]) => c)
  }, [directory.data, location])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 min-h-[var(--tap)] w-full px-4 text-left"
      >
        <MapPin size={16} className="text-[color:var(--accent)] shrink-0" />
        <span className="font-semibold truncate">{location}</span>
        <ChevronDown size={16} className="text-[color:var(--fg-faint)] shrink-0" />
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title="Ubicación">
        <div className="flex flex-col gap-4">
          {/* "Usar mi ubicación actual" lived here and was the accent-coloured
              first item of the sheet. It set `location` to the literal string
              "Cerca de mí", which `cityKeyForSpain` turned into the city key
              `es_cerca de mí` — a key no match can ever carry. The feed went
              empty, and because the choice persists in module-scope feed
              memory it STAYED empty across navigation with no affordance to
              undo it. Removed rather than repaired: it cannot work until real
              geolocation ships (docs/location-system.md), and the same
              reasoning retired the radius filter earlier. A control that
              implies coverage the app does not have is worse than its absence.

              Restore it with the geolocation feature, not before. */}
          <div className="flex flex-col">
            <p className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)] px-1 pb-1">Zonas</p>
            {cities.map((loc) => {
              const active = loc === location
              return (
                <button
                  key={loc}
                  type="button"
                  onClick={() => {
                    onLocationChange(loc)
                    setOpen(false)
                  }}
                  className="flex items-center justify-between min-h-[var(--tap)] px-1 rounded-[var(--radius)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform"
                >
                  <span className="font-medium">{loc}</span>
                  {active && <Check size={18} className="text-[color:var(--accent)]" />}
                </button>
              )
            })}
          </div>

          {/* RADIO PICKER REMOVED 2026-08-08 — it was a no-op that looked
              like a filter. Real matches carry `distanceKm: null` until geohash
              proximity ships (see MatchView's own honesty comment), so
              `distanceKm == null || distanceKm <= radiusKm` admitted everything
              and the control silently did nothing. It only appeared to work in
              reviews because the MOCK fixtures do carry distances.

              Marta's #1 job is "find a match near me", so a radius control that
              implies coverage it doesn't have is exactly the trust defect this
              app keeps retiring. The location picker above still works — that
              one really does re-centre the feed. Restore this with #proximity,
              together with the geohash range query it needs.

              `radiusKm` stays in the hook (defaulted 10) so the plumbing and
              the empty-state copy survive intact for that day. */}
        </div>
      </BottomSheet>
    </>
  )
}
